import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * Shared context for optimization/conversion scope across tabs.
 * This allows Optimization and Electrification tabs to share the same
 * vehicle/type/station selection state AND available options.
 */
interface OptimizationScopeContextType {
  // Selected scope
  selectedVehicles: string[];
  selectedVehicleTypes: string[];
  selectedStations: string[];
  
  // Available options (populated by any tab that has data)
  availableVehicles: string[];
  availableVehicleTypes: string[];
  availableStations: string[];
  
  // Setters for selections
  setSelectedVehicles: (vehicles: string[]) => void;
  setSelectedVehicleTypes: (types: string[]) => void;
  setSelectedStations: (stations: string[]) => void;
  
  // Setters for available options (merge with existing)
  addAvailableVehicles: (vehicles: string[]) => void;
  addAvailableVehicleTypes: (types: string[]) => void;
  addAvailableStations: (stations: string[]) => void;
  
  // Convenience methods
  clearAllSelections: () => void;
  hasAnySelection: boolean;
}

const OptimizationScopeContext = createContext<OptimizationScopeContextType | undefined>(undefined);

export function OptimizationScopeProvider({ children }: { children: ReactNode }) {
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<string[]>([]);
  const [selectedStations, setSelectedStations] = useState<string[]>([]);
  
  const [availableVehicles, setAvailableVehicles] = useState<string[]>([]);
  const [availableVehicleTypes, setAvailableVehicleTypes] = useState<string[]>([]);
  const [availableStations, setAvailableStations] = useState<string[]>([]);
  
  const clearAllSelections = () => {
    setSelectedVehicles([]);
    setSelectedVehicleTypes([]);
    setSelectedStations([]);
  };
  
  const hasAnySelection = 
    selectedVehicles.length > 0 || 
    selectedVehicleTypes.length > 0 || 
    selectedStations.length > 0;
  
  // Merge new available options with existing ones (deduped and sorted)
  const addAvailableVehicles = useCallback((vehicles: string[]) => {
    setAvailableVehicles(prev => {
      const merged = new Set([...prev, ...vehicles]);
      return Array.from(merged).sort();
    });
  }, []);
  
  const addAvailableVehicleTypes = useCallback((types: string[]) => {
    setAvailableVehicleTypes(prev => {
      const merged = new Set([...prev, ...types]);
      return Array.from(merged).sort();
    });
  }, []);
  
  const addAvailableStations = useCallback((stations: string[]) => {
    setAvailableStations(prev => {
      const merged = new Set([...prev, ...stations]);
      return Array.from(merged).sort();
    });
  }, []);

  return (
    <OptimizationScopeContext.Provider value={{
      selectedVehicles,
      selectedVehicleTypes,
      selectedStations,
      availableVehicles,
      availableVehicleTypes,
      availableStations,
      setSelectedVehicles,
      setSelectedVehicleTypes,
      setSelectedStations,
      addAvailableVehicles,
      addAvailableVehicleTypes,
      addAvailableStations,
      clearAllSelections,
      hasAnySelection,
    }}>
      {children}
    </OptimizationScopeContext.Provider>
  );
}

export function useOptimizationScope() {
  const context = useContext(OptimizationScopeContext);
  if (context === undefined) {
    throw new Error('useOptimizationScope must be used within an OptimizationScopeProvider');
  }
  return context;
}

